import React, {useEffect, useMemo, useState} from "react";
import * as echarts from 'echarts/core';
import { BarChart } from 'echarts/charts';
import {GridComponent, TooltipComponent} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import GotoTrangleSvg from "../../../components/svg/GotoTrangleSvg";
import fetchService from "../../../utils/service";
import styles from './styles.module.scss';
import {useModal} from "../../../components/RegModal/ModalContext";

echarts.use([GridComponent, TooltipComponent, BarChart, CanvasRenderer]);

interface FundingChartType {
  title: string;
  update_date: string;
  xaxis: string[];
  yaxis: number[];
}

interface StatsMarketFundingType {
  stats_name: string;
  change_ratio: number;
  stats_count: number;
}

interface InvestorType {
  funding_count: number;
  investor_code: string;
  investor_description: string;
  investor_logo: string;
  investor_name: string;
}

interface StatsTagType {
  funding_count: number;
  tag_name: string;
}

interface TrendDataType {
  stats_month: string;
  update_date: string;
  fundingChartVO: FundingChartType;
  statsMarketFundingList: StatsMarketFundingType[];
  investor: InvestorType;
  statsTagList: StatsTagType[]
}

export default function Trends() {

  const [data, setData] = useState<TrendDataType>()
  const { showModal } = useModal();
  useEffect(() => {
    fetchService<{result: TrendDataType }>("/api/vip/free_home/get_home_info")
      .then(res => {
        setData(res.result)
      })
  }, [])

  useEffect(() => {
    if(data?.fundingChartVO) {
      const chart = echarts.init(document.getElementById('trend'));
      chart.setOption({
        tooltip: {
          show: true,
          formatter: "时间：{b}<br />投资事件数：{c}"
        },
        grid: {
          top: '30px',
          left: '20px',
          right: 0,
          bottom: 0,
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: data.fundingChartVO.xaxis,
          axisLabel: { interval: 0, rotate: 45, color: '#858A99' },
          axisLine: {lineStyle: {color: '#C4CFE7'}}
        },
        yAxis: {
          type: 'value',
          axisTick: {show: false},
          splitLine: {lineStyle: {color: '#C4CFE7', type: 'dashed'}}
        },
        series: [{
          type: 'bar',
          data: data.fundingChartVO.yaxis,
          barWidth: 18,
          itemStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: '#004EFC' },
              { offset: 1, color: 'rgba(0, 78, 252, 0)' }
            ])
          }
        }]
      });
    }
  }, [data?.fundingChartVO])

  const update_date = useMemo(() => {
    if(data?.update_date) {
      return data.update_date.substring(0, 10)
    } else return ""
  }, [data?.update_date])

  const stats_month = useMemo(() => {
    if(data?.stats_month) {
      return Number(data.stats_month.substring(5, 7))
    } else return ""
  }, [data?.stats_month])

  return (
    <section className={styles.trends}>
      <h2>国内创投市场趋势</h2>

      <div className={`${styles.flexbox} ${styles.mt48}`} onClick={showModal}>
        <section style={{flex: 2}}>
          <h4>投资事件数</h4>
          <desc>更新于{update_date}</desc>
          <div id="trend" style={{width: "100%", height: "320px"}} />
        </section>
        <section style={{flex: 1}}>
          <h4>{stats_month}月市场回顾</h4>
          <desc>更新于{update_date}</desc>
          <div className={styles.mt40}>
            <p className={styles.count} style={{fontSize: "64px"}}>{data?.statsMarketFundingList?.[0].stats_count}</p>
            <p><span className={styles.label}>较上周期</span><span className={styles[`${data?.statsMarketFundingList?.[0].change_ratio > 0 ? "red" : "green"}`]}>{Math.abs(data?.statsMarketFundingList?.[0].change_ratio)}%</span></p>
            <h5>{data?.statsMarketFundingList?.[0].stats_name}</h5>
          </div>
          <div className={styles.mt40}>
            <div style={{display:"inline-block"}}>
              <p className={styles.count} style={{fontSize: "48px"}}>{data?.statsMarketFundingList?.[1].stats_count}</p>
              <p><span className={styles.label}>较上周期</span><span className={styles[`${data?.statsMarketFundingList?.[1].change_ratio > 0 ? "red" : "green"}`]}>{Math.abs(data?.statsMarketFundingList?.[1].change_ratio)}%</span></p>
              <h5>{data?.statsMarketFundingList?.[1].stats_name}</h5>
            </div>
            <div style={{display:"inline-block", marginLeft: "55px"}}>
              <p className={styles.count} style={{fontSize: "48px"}}>{data?.statsMarketFundingList?.[2].stats_count}</p>
              <p><span className={styles.label}>较上周期</span><span className={styles[`${data?.statsMarketFundingList?.[2].change_ratio > 0 ? "red" : "green"}`]}>{Math.abs(data?.statsMarketFundingList?.[2].change_ratio)}%</span></p>
              <h5>{data?.statsMarketFundingList?.[2].stats_name}</h5>
            </div>
          </div>
        </section>

      </div>
      <div className={styles.flexbox} onClick={showModal}>
        <section style={{flex: 1}}>
          <h4>{stats_month}月最活跃机构</h4>
          <desc>更新于{update_date}</desc>
          <div style={{marginTop: "16px"}}>
            <div className={styles.logo}>
              <div className={"flexbox"}>
                <img src={data?.investor?.investor_logo} alt="investor logo"/>

              </div>
            </div>
            <div style={{display: "inline-block", marginLeft: "20px", verticalAlign: "middle"}}>
              <p className={styles.count} style={{fontSize: "24px", fontWeight:"bold"}}>{data?.investor?.investor_name}</p>
              <p style={{marginTop: "2px"}}>投资事件数：{data?.investor?.funding_count}次</p>
            </div>
            <p className={styles.desc}>
              {data?.investor?.investor_description}
            </p>
          </div>
        </section>
        <section style={{flex: 1, width: "50%", minHeight: "330px"}}>
          <h4>{stats_month}月热门行业</h4>
          <desc>更新于{update_date}</desc>
          <div className={styles.hangye}>
            <div className={styles.l}>
              {data?.statsTagList?.map((t, i) => <div key={`tag${i}`} className={styles.tag}>
                <p className={styles.count} style={{fontSize: "22px"}}>{t.tag_name}</p>
                <span>投资事件数：{t.funding_count}次</span>
              </div>)}
            </div>

          </div>
        </section>
      </div>
    </section>
  )

}
